import { addWeeks, format, getDay, startOfDay } from "date-fns";

export interface ScheduleData {
  type?: "recurring" | "one_off";
  weekly: Record<string, string[]>;
  exceptions: { date: string; slots: string[]; isClosed: boolean }[];
}

export type OccurrenceStatus =
  | "available"
  | "business_closed"
  | "no_slot"
  | "full"
  | "past";

export interface PlannedOccurrence {
  position: number;
  date: Date;
  dateISO: string;
  status: OccurrenceStatus;
  remainingSpots: number;
}

const DAY_NAMES = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export function computeOccurrenceDates(start: Date, weeks: number): Date[] {
  const out: Date[] = [];
  const base = startOfDay(start);
  for (let i = 0; i < weeks; i++) {
    out.push(addWeeks(base, i));
  }
  return out;
}

export interface EvaluateContext {
  schedule: ScheduleData | null;
  capacityForDate: Record<string, number>;
  totalSpots: number;
  pax: number;
  selectedTime12h: string;
}

export function evaluateOccurrence(
  date: Date,
  ctx: EvaluateContext,
): { status: OccurrenceStatus; remainingSpots: number } {
  const dateISO = format(date, "yyyy-MM-dd");
  const { schedule, capacityForDate, totalSpots, pax, selectedTime12h } = ctx;

  if (date < startOfDay(new Date())) {
    return { status: "past", remainingSpots: 0 };
  }

  if (!schedule) {
    return { status: "available", remainingSpots: totalSpots };
  }

  const exception = schedule.exceptions.find((e) => e.date === dateISO);
  if (exception?.isClosed) {
    return { status: "business_closed", remainingSpots: 0 };
  }

  let raw: string[] = [];
  if (schedule.type === "one_off") {
    if (!exception || exception.slots.length === 0) {
      return { status: "no_slot", remainingSpots: 0 };
    }
    raw = exception.slots;
  } else if (exception) {
    raw = exception.slots;
  } else {
    const dayName = DAY_NAMES[getDay(date)];
    raw = schedule.weekly[dayName] || [];
  }

  // Slots are stored as 24h strings (e.g. "15:00"). The selected time the
  // customer originally picked is the 12h display form (e.g. "3:00 PM").
  // Convert to 12h for comparison.
  const matches = raw.some((t24) => to12h(t24) === selectedTime12h);
  if (!matches) return { status: "no_slot", remainingSpots: 0 };

  const booked = capacityForDate[dateISO] ?? 0;
  const remaining = Math.max(0, totalSpots - booked);
  if (remaining < pax) return { status: "full", remainingSpots: remaining };
  return { status: "available", remainingSpots: remaining };
}

export function to12h(t24: string): string {
  const [hStr, mStr] = t24.split(":");
  let h = parseInt(hStr, 10) || 0;
  const m = mStr || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
}

export function to24h(t12: string): string {
  const m = t12.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!m) return t12;
  let h = parseInt(m[1], 10) || 0;
  const mins = m[2];
  const ampm = (m[3] || "").toUpperCase();
  if (ampm === "PM" && h < 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${mins}`;
}

/**
 * Customer-facing label for a single occurrence's availability. Plain-English
 * assume the reader has never seen the word "slot" outside of a casino. For
 * "full" we surface concrete numbers when we have them so the customer can
 * decide whether to drop pax or pick a different starting week.
 */
export function describeStatus(
  s: OccurrenceStatus,
  ctx?: { remainingSpots?: number; pax?: number },
): string {
  switch (s) {
    case "available":
      return "Available";
    case "business_closed":
      return "Business is closed";
    case "no_slot":
      return "This time isn't offered";
    case "full": {
      const remaining = ctx?.remainingSpots ?? 0;
      const needed = ctx?.pax ?? 0;
      if (remaining > 0 && needed > 0) {
        return `Only ${remaining} ${remaining === 1 ? "spot" : "spots"} left (you need ${needed})`;
      }
      if (remaining > 0) {
        return `Only ${remaining} ${remaining === 1 ? "spot" : "spots"} left`;
      }
      return "Fully booked";
    }
    case "past":
      return "This date has already passed";
  }
}

/**
 * Given the planned occurrences and the resolution choice, return the rows
 * that will actually be booked.
 *
 * - "skip". Drop any non-available rows, keep the rest at their original
 *   positions (positions stay 1..N for stable identity).
 * - "trim". Keep only the leading contiguous run of available rows starting
 *   from position 1. If position 1 is unavailable, returns [].
 */
export function applyConflictResolution(
  planned: PlannedOccurrence[],
  resolution: "skip" | "trim" | "none",
): PlannedOccurrence[] {
  if (resolution === "none") return planned;
  if (resolution === "skip") {
    return planned.filter((p) => p.status === "available");
  }
  // trim
  const out: PlannedOccurrence[] = [];
  for (const p of planned) {
    if (p.status !== "available") break;
    out.push(p);
  }
  return out;
}
